import { useSession } from '@/auth/useSession';
import { FormModal, SubmitProps } from '@/components/form-modal';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useToast } from '@/components/ui/hooks/use-toast';
import { emptySelectOption } from '@/components/ui/multi-select-utils';
import {
  createCoachPauseReassignmentRequests,
  getCoachPauseReassignmentPreview,
  getPendingStudentGroupReassignmentRequests,
} from '@/lib/api-calls';
import { airServerErrorFrom } from '@/lib/error-handling';
import { pluralize } from '@/lib/string-utils';
import {
  getReassignmentRequestFormDefaultValues,
  ReassignmentRequestFormValues,
  reassignmentRequestFormSchema,
} from '@/lib/student-group-reassignment-utils';
import { ReassignmentRequestForm } from '@/routes/_layout/student-groups/-components/reassignment-request-form';
import {
  CoachSubStatus,
  StudentGroupReassignmentReasonLabelMap,
  studentGroupReassignmentReasonSchema,
} from '@airreading/common/data';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Info } from 'lucide-react';
import { useMemo } from 'react';
import { defaultReasonForPauseSubStatuses } from './coach-pause-reassignment';

export interface CoachPauseReassignmentModalProps {
  coachId: string;
  pauseSubStatuses: CoachSubStatus[];
  onClose: () => void;
}

export function CoachPauseReassignmentModal({ coachId, pauseSubStatuses, onClose }: CoachPauseReassignmentModalProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { actingUser } = useSession();

  const { data: preview, isPending: isPreviewPending } = useQuery(getCoachPauseReassignmentPreview(coachId));

  const { mutateAsync } = useMutation({
    mutationFn: createCoachPauseReassignmentRequests,
  });

  const defaultReason = defaultReasonForPauseSubStatuses(pauseSubStatuses);
  const defaultValues = useMemo(
    () =>
      getReassignmentRequestFormDefaultValues({
        requester: actingUser
          ? { value: actingUser.id, label: actingUser.fullName ?? actingUser.id, disable: false }
          : emptySelectOption,
        reassignmentReasons: [
          {
            value: defaultReason,
            label: StudentGroupReassignmentReasonLabelMap[defaultReason],
          },
        ],
      }),
    [actingUser, defaultReason],
  );

  const activeGroupCount = preview?.activeStudentGroupCount ?? 0;
  const lastCompletedLabel = preview?.lastCompletedSessionPlainDate
    ? preview.lastCompletedSessionPlainDate.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    : '—';

  const onSubmit = async ({ formData, closeModal }: SubmitProps<ReassignmentRequestFormValues>) => {
    try {
      const result = await mutateAsync({
        coachId,
        effectivePlainDate: formData.effectiveDate,
        requesterId: formData.requester.value,
        reasons: formData.reassignmentReasons.map(({ value }) => studentGroupReassignmentReasonSchema.parse(value)),
        preferredCoachId: formData.preferredNewTeacher.value.trim() || null,
        specialRequirements: formData.specialRequirements?.trim() || null,
      });

      const descriptionParts = [`${result.createdCount} reassignment request(s) created`];
      if (result.skippedAlreadyPendingCount > 0) {
        descriptionParts.push(`${result.skippedAlreadyPendingCount} skipped (pending request already existed)`);
      }
      if (result.skippedEffectiveDateAfterEndCount > 0) {
        descriptionParts.push(`${result.skippedEffectiveDateAfterEndCount} skipped (effective date after group end)`);
      }

      toast({
        title: 'Success',
        description: `${descriptionParts.join('. ')}.`,
      });

      await queryClient.invalidateQueries(getPendingStudentGroupReassignmentRequests());
      closeModal();
      onClose();
    } catch (error) {
      toast({
        title: 'Error',
        description: airServerErrorFrom(error)?.userFacingMessage ?? 'Failed to create reassignment requests',
        variant: 'destructive',
      });
    }
  };

  const groupLabel = isPreviewPending
    ? '…'
    : `${activeGroupCount} ${pluralize(activeGroupCount, 'student group', 'student groups')}`;

  return (
    <FormModal
      modalTitle="Create reassignment requests"
      modalDescription={`Opening requests for ${groupLabel}`}
      formSchema={reassignmentRequestFormSchema}
      defaultValues={defaultValues}
      resetOnDefaultValuesChange
      onSubmit={onSubmit}
      onClose={onClose}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      trigger={<span className="sr-only">Create reassignment requests</span>}
      cancelBtnLabel="Skip reassignment"
      confirmBtnLabel="Create reassignment requests"
      cancelButtonProps={{ variant: 'ghost', className: 'font-medium text-muted-foreground' }}
      initialOpen
      preventExternalClose
      headerClassName="space-y-1 border-b px-6 pb-4 pt-6"
      footerClassName="border-t px-6 py-4"
      dialogContentProps={{ className: 'max-w-lg gap-0 p-0 lg:max-w-lg' }}
    >
      {({ form }) => (
        <>
          <div className="px-6 pb-0 pt-4">
            <p className="text-sm">
              <span className="font-semibold">Last completed session date</span>{' '}
              <span>{isPreviewPending ? '…' : lastCompletedLabel}</span>
            </p>
          </div>
          <div className="space-y-4 px-6 py-4">
            <ReassignmentRequestForm form={form} className="pt-0" />
            <Alert className="border-blue-100 bg-blue-50 text-blue-600 [&>svg]:text-blue-600">
              <Info className="h-4 w-4" />
              <AlertDescription>
                These settings will apply to all {groupLabel}. You can edit individual requests after they are created.
              </AlertDescription>
            </Alert>
          </div>
        </>
      )}
    </FormModal>
  );
}
